// Replace with your real type import

import { IMcBimItem } from "@backend/src/shared/schemas/schemas";
import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import { createSelector } from "@reduxjs/toolkit";
import { selectHttpProject } from "../projectSelectors";
import { GetMcbimItemsInSpaceApiArg, openapi } from "../../openapi";
import { IRootState } from "../app/store";
import { selectProjectNo } from "../globalSelectors";
import { selectSelectedSpaceNumber } from "../sharedSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";

export interface ICompCodeCount {
  compCode: string;
  count: number;
  items: IMcBimItem[]; // handy for table drilldown/debug
}

/** Extract compCode from an item using project.compCodePath */
const extractCompCode = (item: IMcBimItem, compCodePath: string): string | null => {
  const raw = getDeeplyNestedValue(item, compCodePath);

  if (raw == null) return null;

  // allow either a plain string or something like { value: "UXA.103" }
  if (typeof raw === "string") {
    const s = raw.trim();
    return s.length ? s : null;
  }

  if (typeof raw === "object") {
    const maybeValue = (raw as any).value;
    if (typeof maybeValue === "string") {
      const s = maybeValue.trim();
      return s.length ? s : null;
    }
  }

  return null;
};

export const selectMcBimItemsInSelectedRoomArgs = createSelector(
  [selectSelectedSpaceNumber, selectProjectNo],
  (selectedSpaceNumber, projectNo): GetMcbimItemsInSpaceApiArg | null => {
    if (!selectedSpaceNumber || !projectNo) return null;

    return {
      projectNo,
      spaceName: selectedSpaceNumber,
    };
  }
);

export const selectHttpMcBimItemsInSelectedRoom = createSelector(
  [selectMcBimItemsInSelectedRoomArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return [];
    const resp = openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data;

    return resp ?? [];
  }
);

export const selectCompCodeCountsInSelectedRoom = createSelector(
  [selectHttpMcBimItemsInSelectedRoom, selectHttpProject],
  (items, project): ICompCodeCount[] => {
    const compCodePath = project?.compCodePath;
    if (!compCodePath) return [];

    const map = new Map<string, ICompCodeCount>();

    for (const item of items ?? []) {
      const compCode = extractCompCode(item, compCodePath);
      if (!compCode) continue;

      const existing = map.get(compCode);
      if (existing) {
        existing.count += 1;
        existing.items.push(item);
      } else {
        map.set(compCode, { compCode, count: 1, items: [item] });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.compCode.localeCompare(b.compCode));
  }
);

export interface IArticleCompareRow {
  compCode: string;

  // Spec (room.specifiedArticles)
  specifiedQty: number;
  specifiedName?: string;
  specifiedCategory?: string;
  specifiedResponsibility?: string;

  // Modell (MCBIM items i rummet)
  modelQty: number;

  // Diff / status
  delta: number; // modelQty - specifiedQty
  status: "OK" | "MISSING_IN_MODEL" | "EXTRA_IN_MODEL" | "MISMATCH_QTY";

  // Valfritt (bra för drilldown/debug i tabell)
  modelItems?: any[];
}

export const selectArticleCompareRowsInSelectedRoom = createSelector(
  [selectHttpSingleRoomFull, selectCompCodeCountsInSelectedRoom],
  (room, modelCounts): IArticleCompareRow[] => {
    const specifiedArticles = room?.specifiedArticles ?? [];

    // 1) Spec -> map
    const specMap = new Map<
      string,
      {
        quantity: number;
        name?: string;
        category?: string;
        responsibility?: string;
      }
    >();

    for (const a of specifiedArticles) {
      if (!a?.compCode) continue;
      specMap.set(a.compCode, {
        quantity: a.quantity ?? 0,
        name: a.name,
        category: a.category,
        responsibility: a.responsibility,
      });
    }

    // 2) Modell -> map
    const modelMap = new Map<string, { count: number; items?: any[] }>();
    for (const m of modelCounts ?? []) {
      modelMap.set(m.compCode, { count: m.count, items: m.items });
    }

    // 3) Union av compCodes
    const keys = new Set<string>([...Array.from(specMap.keys()), ...Array.from(modelMap.keys())]);

    const rows: IArticleCompareRow[] = [];

    for (const compCode of keys) {
      const spec = specMap.get(compCode);
      const model = modelMap.get(compCode);

      const specifiedQty = spec?.quantity ?? 0;
      const modelQty = model?.count ?? 0;
      const delta = modelQty - specifiedQty;

      let status: IArticleCompareRow["status"];
      if (specifiedQty > 0 && modelQty === 0) status = "MISSING_IN_MODEL";
      else if (specifiedQty === 0 && modelQty > 0) status = "EXTRA_IN_MODEL";
      else if (specifiedQty === modelQty) status = "OK";
      else status = "MISMATCH_QTY";

      rows.push({
        compCode,
        specifiedQty,
        specifiedName: spec?.name,
        specifiedCategory: spec?.category,
        specifiedResponsibility: spec?.responsibility,
        modelQty,
        delta,
        status,
        modelItems: model?.items,
      });
    }

    rows.sort((a, b) => a.compCode.localeCompare(b.compCode));
    return rows;
  }
);
